import React, { useState, useContext, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  Breadcrumbs,
  Link,
  alpha,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import PersonIcon from "@mui/icons-material/Person";
import PaymentIcon from "@mui/icons-material/Payment";
import BusinessIcon from "@mui/icons-material/Business";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import TuneIcon from "@mui/icons-material/Tune";

import { AuthContext } from "../contexts/AuthContext";
import ProfilePage from "./Settings/ProfilePage";
import OrganizationPage from "./Settings/OrganizationPage";
import Preferences from "./Settings/Preferences";
import ChangePasswordTab from "../components/Settings/ChangePasswordTab";
import BillingPanel from "./Settings/BillingPanel";
import {
  SETTING_BILLING,
  SETTING_CHANGE_PASSWORD,
  SETTING_ORGANIZATION,
  SETTING_PREFERENCES,
  SETTING_PROFILE,
} from "../utils/data/controlData";

const renderTabLabel = (tab) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    {tab.label}
    {tab.wip && (
      <Typography component='span' variant='caption'>
        WIP
      </Typography>
    )}
  </Box>
);

const SettingsPage = () => {
  const { global_authState, checkAccess } = useContext(AuthContext);
  const [tabIndex, setTabIndex] = useState(0);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const handleTabChange = (_, newIndex) => {
    setTabIndex(newIndex);
  };

  const handleGoToBilling = () => {
    setTabIndex(4);
  };

  // Check access permissions for different tabs
  const canAccessProfile = checkAccess("view", SETTING_PROFILE);
  const canAccessOrganization = checkAccess("view", SETTING_ORGANIZATION);
  const canAccessChangePassword = checkAccess("view", SETTING_CHANGE_PASSWORD);
  const canAccessPreferences = checkAccess("view", SETTING_PREFERENCES);
  const canAccessBilling = checkAccess("view", SETTING_BILLING);

  const tabs = useMemo(
    () => [
      {
        value: 0,
        icon: <PersonIcon />,
        label: "Profile",
        wip: false,
        disabled: !canAccessProfile,
      },
      {
        value: 1,
        icon: <LockIcon />,
        label: "Change Password",
        wip: false,
        disabled: !canAccessChangePassword,
      },
      {
        value: 2,
        icon: <BusinessIcon />,
        label: "Organization",
        wip: false,
        disabled: !canAccessOrganization,
      },
      {
        value: 3,
        icon: <TuneIcon />,
        label: "Preferences",
        wip: false,
        disabled: !canAccessPreferences,
      },
      {
        value: 4,
        icon: <PaymentIcon />,
        label: "Billing",
        wip: false,
        disabled: !canAccessBilling,
      },
    ],
    [
      canAccessProfile,
      canAccessOrganization,
      canAccessChangePassword,
      canAccessPreferences,
      canAccessBilling,
    ],
  );

  // Filter out disabled tabs
  const availableTabs = useMemo(
    () => tabs.filter((tab) => !tab.disabled),
    [tabs],
  );

  // Ensure tab param is updated if tabIndex changes, and initialize from param on mount
  React.useEffect(() => {
    // On mount, set tab index from param
    const searchParams = new URLSearchParams(window.location.search);
    const tab = searchParams.get("tab");

    // Determine if this should be treated as "billing" tab even without the tab param
    let isBilling = false;
    // URLSearchParams does not preserve order, so we check for "type", "reference", and "status" as well as their typical values
    // (You can make this more robust as needed for your specific business logic)
    if (
      tab === "billing" ||
      tab === "billings" ||
      (!tab &&
        searchParams.has("type") &&
        searchParams.has("reference") &&
        searchParams.has("status"))
    ) {
      isBilling = true;
    }

    let newIndex = 0; // default to Profile (index 1)
    if (tab === "profile") {
      newIndex = 0;
    } else if (tab === "organization") {
      newIndex = 2;
    } else if (tab === "preferences") {
      newIndex = 3;
    } else if (tab === "security") {
      newIndex = 1;
    } else if (isBilling) {
      newIndex = 4;
    }
    setTabIndex(newIndex);
  }, []);

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 70px)", // 根据你的顶部导航栏高度微调
        width: "100%",
        display: "flex",
        flexDirection: "column",
        px: { xs: 2, sm: 4 },
        py: { xs: 3, sm: 5 },
        bgcolor: (theme) => theme.palette.grey[50], // 1. 增加极淡的背景色，突出层级
      }}
    >
      {/* Header Section */}
      <Box>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize='small' />}
          sx={{ mb: 2 }}
        >
          <Link
            underline='hover'
            color='text.secondary'
            sx={{
              display: "flex",
              alignItems: "center",
              fontWeight: 500,
              "&:hover": { color: "primary.main" },
            }}
            href='/consolePanel'
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize='inherit' />
            Home
          </Link>
          <Typography color='text.primary' sx={{ fontWeight: 600 }}>
            Settings
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* Main Card */}
      <Paper
        elevation={0}
        sx={{
          flexGrow: 1,
          borderRadius: 3,
          overflow: "hidden",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          border: "1px solid",
          borderColor: (theme) => alpha(theme.palette.divider, 0.3),
          bgcolor: "background.paper",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.03)", // 2. 增加极轻微的阴影
        }}
      >
        {/* Tabs Panel (Sidebar) */}
        <Box
          sx={{
            flexBasis: { xs: "auto", sm: 280 },
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.02),
            borderRight: {
              xs: "none",
              sm: `1.5px solid ${alpha(theme.palette.primary.main, 0.13)}`,
            },
            borderBottom: { xs: "1px solid", sm: "none" },
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.1),
            boxShadow: {
              xs: "none",
              sm: "4px 0 24px -10px rgba(25, 118, 210, 0.07)",
            },
            p: 2,
            minHeight: { sm: 480 },
            zIndex: 2,
          }}
        >
          <Tabs
            orientation={isSmallScreen ? "horizontal" : "vertical"}
            value={tabIndex}
            onChange={handleTabChange}
            variant='scrollable'
            scrollButtons='auto'
            allowScrollButtonsMobile
            sx={{
              minHeight: { sm: 400 },
              // 3. 优化 Indicator 样式，使其更贴合 Tab
              ".MuiTabs-indicator": {
                left: isSmallScreen ? "unset" : 0,
                right: isSmallScreen ? "unset" : "unset",
                width: isSmallScreen ? "100%" : "3px",
                height: isSmallScreen ? "3px" : "100%",
                bottom: isSmallScreen ? 0 : "unset",
                borderRadius: isSmallScreen ? "4px 4px 0 0" : "0 4px 4px 0",
                bgcolor: "primary.main",
              },
              ".MuiTab-root": {
                justifyContent: "flex-start",
                minHeight: 48,
                textAlign: "left",
                alignItems: "center",
                textTransform: "none",
                px: 2,
                py: 1.5,
                fontSize: "0.95rem",
                color: "text.secondary",
                borderRadius: 1.5, // 增加圆角
                mb: { sm: 0.5 }, // 移动端横向排列时去掉间距
                "&:hover": {
                  backgroundColor: (theme) =>
                    alpha(theme.palette.primary.main, 0.06),
                  color: "primary.main",
                },
                "&.Mui-selected": {
                  color: "primary.main",
                  fontWeight: 600,
                  backgroundColor: (theme) =>
                    alpha(theme.palette.primary.main, 0.1),
                },
              },
            }}
          >
            {availableTabs.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                icon={tab.icon}
                iconPosition='start'
                label={renderTabLabel(tab)}
                disabled={tab.disabled}
              />
            ))}
          </Tabs>
        </Box>

        {/* Content Panel */}
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            p: { xs: 3, sm: 4 }, // 4. 增加内容区域内边距，避免内容贴边
            bgcolor: "background.paper",
            // 自定义滚动条样式（可选）
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "#ccc",
              borderRadius: "4px",
            },
          }}
        >
          {tabIndex === 0 && canAccessProfile && (
            <ProfilePage onGoToBilling={handleGoToBilling} />
          )}
          {tabIndex === 2 && canAccessOrganization && (
            <OrganizationPage canAccess={canAccessOrganization} />
          )}
          {tabIndex === 1 && canAccessChangePassword && (
            <ChangePasswordTab email={global_authState.email} />
          )}
          {tabIndex === 3 && canAccessPreferences && <Preferences />}
          {tabIndex === 4 && canAccessBilling && <BillingPanel />}

          {/* 5. 优化空状态展示 */}
          {availableTabs.length === 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                py: 8,
                textAlign: "center",
                color: "text.secondary",
              }}
            >
              <Box
                sx={{
                  p: 2,
                  bgcolor: "grey.100",
                  borderRadius: "50%",
                  mb: 2,
                }}
              >
                <LockIcon sx={{ fontSize: 40 }} />
              </Box>
              <Typography variant='h6' fontWeight={600} gutterBottom>
                Access Restricted
              </Typography>
              <Typography variant='body2' sx={{ maxWidth: 300 }}>
                You currently don't have the necessary permissions to view or
                edit these settings.
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default SettingsPage;
